import Box from "@material-ui/core/Box";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import clsx from "clsx";
import { Form, FormikProps } from "formik";
import React, { useRef, useState } from "react";
import PagedResponse from "../../../../../api/models/PagedResponse";
import ProductReferenceResponse from "../../../../../api/models/ProductReferenceResponse";
import TestRequestResponse from "../../../../../api/models/test-request/TestRequestResponse";
import UserReferenceResponse from "../../../../../api/models/UserReferenceResponse";
import BackgroundInformationApi from "../../../../../api/services/BackgroundInformationApi";
import { AutocompleteSelectable } from "../../../../../components/Autocomplete/CustomSearchAutoComplete";
import {
  CountrySearchAutocomplete,
  CustomerSearchAutocomplete,
} from "../../../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import CustomButton from "../../../../../components/Button/CustomButton";
import { ConfirmationDialogParams } from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialog";
import InfoBox from "../../../../../components/InfoBox";
import CustomDatePicker from "../../../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../../../components/Input/CustomDropdown";
import CustomTextField from "../../../../../components/Input/CustomTextField";
import { MASK_NUMBER, MASK_THOUSANDS_SEPARATOR } from "../../../../../components/Input/MaskConfigurations";
import { useLaboratories } from "../../../../../contexts/LaboratoriesContext";
import removeElementAt from "../../../../../functions/removeElementAt.function";
import { getDescriptionShowPrefilled, setDescriptionShowPrefilled } from "../../../../../helper/UserSettingsHelper";
import useEnv from "../../../../../hooks/env";
import { tesaDarkRedError } from "../../../../../styles/colors";
import * as InfoMessages from "../../../messages";
import { infoCscLaboratory } from "../../../messages";
import ApproversInput from "../../inputs/ApproversInput";
import DeadlineInput from "../../inputs/DeadlineInput";
import EstimatedFinishingDateInput from "../../inputs/EstimatedFinishingDateInput";
import InternalContactInput from "../../inputs/InternalContactInput";
import OpportunityProductsInput from "../../inputs/OpportunityProductsInput";
import ProductsInput from "../../inputs/ProductsInput";
import SalesTeamInput from "../../inputs/SalesTeamInput";
import SuggestedApproverInput from "../../inputs/SuggestedApproverInput";
import TestSpecificationInput from "../../inputs/TestSpecificationInput";
import DescriptionFormType from "../../models/Description.form";
import ProductEditData from "../../ProductEditData.model";
import TestRequestGallery from "../../TestRequestGallery";
import * as Dictionaries from "../dictionaries";
import * as Labels from "../fieldLabels";
import * as Fields from "../fieldNames";
import { getCompetitorProductReferences, getPrototypeProductReferences } from "../productReferenceHelper";
import { StyledMenu, StyledMenuItem } from "../../../../../components/Menu/StyledMenu";
import CountryResponse from "../../../../../api/models/country/Country.response";
import CustomerResponse from "../../../../../api/models/customer/Customer.response";
import ProcessTypeInput from "../../inputs/ProcessTypeInput";

const useStyles = makeStyles(() => ({
  productsContainer: {
    "& > * + *": {
      marginTop: "18px",
    },
  },
  testInfoGridContainer: {
    "& .LEAPGrid-item-pb-4": {
      paddingBottom: "4px",
    },
    "& .LEAPGrid-item-pb-0": {
      paddingBottom: 0,
    },
  },
  testProgramSpecificationsContainer: {
    "& > * + *": {
      marginTop: (props: Partial<DescriptionFormSalesProps>) => (props.permissions?.editDescription ? "20px" : "32px"),
    },
  },
}));

type ConfirmationOverride = {
  [K in keyof DescriptionFormType]?: boolean;
};
type TestRequestDescriptionAttachment = Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>;
type TestRequestDescriptionPermissions = Pick<
  TestRequestRaw.Schemas.TestRequestPermissions,
  "description"
>["description"];
type User = TestRequestRaw.Schemas.UserInformation;

interface DescriptionFormSalesProps {
  attachedFilesReferences: TestRequestDescriptionAttachment[];
  formikProps: FormikProps<DescriptionFormType>;
  permissions: TestRequestDescriptionPermissions;
  submissionOrApprovalDate: Date | null;
  testRequestHistorical: boolean;
  testRequestId: string;
  testRequestObj: any;
  user: User | null;
  handleDirtyChange: (reset?: boolean) => void;
  handleSave: (
    values: DescriptionFormType,
    refresh?: boolean | undefined,
    confirmationOverride?: ConfirmationOverride,
  ) => Promise<TestRequestResponse | void>;
  showConfirmationDialog: (params?: ConfirmationDialogParams | undefined) => Promise<string>;
  showSalesProductsDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  showAddPrototypeDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  showCompetitorDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  scrollToError: () => void;
}

const DescriptionFormSales: React.FC<DescriptionFormSalesProps> = ({
  attachedFilesReferences,
  formikProps,
  handleDirtyChange,
  handleSave,
  permissions,
  submissionOrApprovalDate,
  testRequestHistorical,
  testRequestId,
  testRequestObj,
  user,
  scrollToError,
  showConfirmationDialog,
  showSalesProductsDialog,
  showAddPrototypeDialog,
  showCompetitorDialog,
}) => {
  const { errors, handleBlur, handleChange, setFieldValue, touched, values } = formikProps;
  const [showPrefilled, setShowPrefilled] = useState(getDescriptionShowPrefilled());
  const [showSpecificationInput, setShowSpecificationInput] = useState(values.specifications.length > 0);
  const { api } = useEnv();
  const aproe = testRequestObj?.purpose === "APPLICATION_PROCESS";
  const backgroundInformationApi = new BackgroundInformationApi(api);
  const classes = useStyles({ permissions });
  const createMode = testRequestId === "new";
  const dirCustomerSearch = useRef<{ reset: () => void }>(null);
  const newOrDraft = createMode || testRequestObj?.state === "DRAFT";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { labs, labsLoading } = useLaboratories(testRequestObj?.purpose || "DEFAULT");

  const testRequestPrePreparationStatus = ["DRAFT", "SUBMITTED", "FOR_EVALUATION", "FOR_REVIEW", "FOR_APPROVAL"];
  const isStatusPreparationAndFollowing =
    testRequestObj?.status?.state && testRequestPrePreparationStatus.indexOf(testRequestObj.status.state) === -1;
  const hasHighlyConfidential =
    values.projectConfidentiality === "HIGHLY_CONFIDENTIAL" ||
    testRequestObj?.description?.projectConfidentiality === "HIGHLY_CONFIDENTIAL";
  const isConfidentialityReadOnly = !newOrDraft && hasHighlyConfidential;
  const handleDeleteProduct = ({ data }: AutocompleteSelectable<any>, index: number) => {
    if (data?.type) {
      if (data.type === Fields.PROTOTYPE_PRODUCT_REFERENCES) {
        const prototypeProductToDelete =
          values[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
          values[Fields.PROTOTYPE_PRODUCT_REFERENCES].filter((product) => {
            return (
              product.prototypeProductId !== data.prototypeProductId ||
              product.prototypeProductName !== data.prototypeProductName
            );
          });
        setFieldValue(Fields.PROTOTYPE_PRODUCT_REFERENCES, prototypeProductToDelete);
      }
      if (data.type === Fields.COMPETITOR_PRODUCT_REFERENCES) {
        const competitorProductToDelete =
          values[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
          values[Fields.COMPETITOR_PRODUCT_REFERENCES].filter((product) => {
            return (
              product.competitorProductId !== data.competitorProductId ||
              product.competitorProductName !== data.competitorProductName
            );
          });
        setFieldValue(Fields.COMPETITOR_PRODUCT_REFERENCES, competitorProductToDelete);
      }
    } else {
      const productToDelete = removeElementAt(index, values[Fields.PRODUCT_REFERENCES]);
      setFieldValue(Fields.PRODUCT_REFERENCES, productToDelete);
    }

    handleDirtyChange();
  };

  const handleResetProducts = () => {
    setFieldValue(Fields.PRODUCT_REFERENCES, []);
    setFieldValue(Fields.PROTOTYPE_PRODUCT_REFERENCES, []);
    setFieldValue(Fields.COMPETITOR_PRODUCT_REFERENCES, []);
    handleDirtyChange();
  };

  const handleLocalChange = (e: React.ChangeEvent<unknown>) => {
    if (!e) return;
    handleDirtyChange();
    handleChange(e);
  };

  const handleLocalBlur = (e: React.FocusEvent<unknown>) => {
    if (!e) return;
    handleBlur(e);
  };

  const searchLocations = (query: string) => {
    return backgroundInformationApi.searchLocations(query).then((res) => res?.data);
  };

  const handleApproverChange = (approver: UserReferenceResponse | null) => {
    if (approver?.userId !== values[Fields.APPROVER]?.userId) {
      setFieldValue(Fields.APPROVER, approver);
      handleDirtyChange();
    }
  };

  const handleSpecificationToggleChange = () => {
    if (permissions?.editDescription && showSpecificationInput && values.specifications.length > 0) {
      return showConfirmationDialog({
        confirmColor: tesaDarkRedError,
        confirmText: "Remove Specifications",
        text: "Do you really want to remove all Customer Specifications or Standards?",
      })
        .then(() => {
          setShowSpecificationInput(!showSpecificationInput);
          setFieldValue("specifications", []);
          handleDirtyChange();
        })
        .catch(() => {
          // do nothing on dialog close;
        });
    }
    setShowSpecificationInput(!showSpecificationInput);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const products = [
    ...(values[Fields.PRODUCT_REFERENCES] || []),
    ...(getCompetitorProductReferences(values[Fields.COMPETITOR_PRODUCT_REFERENCES]) || []),
    ...(getPrototypeProductReferences(values[Fields.PROTOTYPE_PRODUCT_REFERENCES]) || []),
  ];

  return (
    <Form>
      <>
        <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!showPrefilled}
                color="secondary"
                onChange={() => {
                  setDescriptionShowPrefilled(!showPrefilled);
                  setShowPrefilled(!showPrefilled);
                }}
                name="prefilledToggle"
              />
            }
            label="Hide prefilled fields"
          />
        </Box>
        <h3 style={{ marginTop: "8px" }}>Test Information</h3>
        <Grid
          classes={{ root: classes.testInfoGridContainer }}
          container
          justify="space-between"
          direction="row"
          spacing={4}
        >
          <Grid item xs={12}>
            {aproe ? (
              <CustomTextField
                fullWidth
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.CUSTOMER_PART] && touched[Fields.CUSTOMER_PART])}
                helperText={
                  errors[Fields.CUSTOMER_PART] && touched[Fields.CUSTOMER_PART]
                    ? errors[Fields.CUSTOMER_PART]
                    : "Separate multiple entries with a comma"
                }
                id={Fields.CUSTOMER_PART}
                label={Labels.CUSTOMER_PART}
                name={Fields.CUSTOMER_PART}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.CUSTOMER_PART]}
              />
            ) : (
              <CustomTextField
                fullWidth
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.SUBSTRATES] && touched[Fields.SUBSTRATES])}
                helperText={
                  errors[Fields.SUBSTRATES] && touched[Fields.SUBSTRATES]
                    ? errors[Fields.SUBSTRATES]
                    : "Separate multiple entries with a comma"
                }
                id={Fields.SUBSTRATES}
                label={Labels.SUBSTRATES}
                name={Fields.SUBSTRATES}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.SUBSTRATES]}
              />
            )}
          </Grid>
          <Grid className={clsx("LEAPGrid-item-pb-0", classes.productsContainer)} item lg={6} md={6} sm={12} xs={12}>
            <ProductsInput
              mandatory
              helperText={
                errors[Fields.PRODUCT_REFERENCES] &&
                errors[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
                errors[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
                touched[Fields.PRODUCT_REFERENCES]
                  ? "Select a product"
                  : ""
              }
              error={
                !!(
                  errors[Fields.PRODUCT_REFERENCES] &&
                  errors[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
                  errors[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
                  touched[Fields.PRODUCT_REFERENCES]
                )
              }
              disabled={!permissions?.editDescription}
              label={Labels.PRODUCT_REFERENCES}
              onDelete={handleDeleteProduct}
              onReset={handleResetProducts}
              onTagClick={(e) => {
                if (e?.data?.type) {
                  if (e.data?.type === Fields.PROTOTYPE_PRODUCT_REFERENCES) {
                    return showAddPrototypeDialog(e);
                  }
                  if (e.data?.type === Fields.COMPETITOR_PRODUCT_REFERENCES) {
                    return showCompetitorDialog(e);
                  }
                }
                return showSalesProductsDialog(e);
              }}
              values={products}
            />
            <CustomButton
              add
              aria-controls="customized-menu"
              aria-haspopup="true"
              disabled={!permissions?.editDescription}
              onClick={handleClick}
              text="Add Product"
            />

            <StyledMenu
              id="customized-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showSalesProductsDialog();
                }}
              >
                <ListItemText primary="tesa Product" />
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showAddPrototypeDialog();
                }}
              >
                <ListItemText primary="tesa Prototype" />
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showCompetitorDialog();
                }}
              >
                <ListItemText primary="Competitor Product" />
              </StyledMenuItem>
            </StyledMenu>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <OpportunityProductsInput values={values[Fields.OPPORTUNITY_PRODUCTS]} />
          </Grid>
          <Grid
            item
            lg={6}
            md={6}
            sm={12}
            xs={12}
            className={clsx({
              "LEAPGrid-item-pb-4": !showSpecificationInput,
            })}
          >
            <Grid container className={classes.testProgramSpecificationsContainer}>
              <Grid item xs={12}>
                {aproe ? (
                  <CustomTextField
                    fullWidth
                    multiline
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL])}
                    helperText={
                      errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL]
                        ? errors[Fields.PROCESS_DETAIL]
                        : ""
                    }
                    icon={!!(errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL])}
                    id={Fields.PROCESS_DETAIL}
                    label={Labels.PROCESS_DETAIL}
                    name={Fields.PROCESS_DETAIL}
                    onBlur={handleBlur}
                    onChange={handleLocalChange}
                    rows={5}
                    value={values[Fields.PROCESS_DETAIL]}
                  />
                ) : (
                  <InfoBox alignTop text={InfoMessages.TEST_PROGRAM}>
                    <CustomTextField
                      fullWidth
                      multiline
                      disabled={!permissions?.editDescription}
                      error={!!(errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM])}
                      helperText={
                        errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM] ? errors[Fields.TEST_PROGRAM] : ""
                      }
                      icon={!!(errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM])}
                      id={Fields.TEST_PROGRAM}
                      label={Labels.TEST_PROGRAM}
                      name={Fields.TEST_PROGRAM}
                      onBlur={handleBlur}
                      onChange={handleLocalChange}
                      rows={5}
                      value={values[Fields.TEST_PROGRAM]}
                    />
                  </InfoBox>
                )}
              </Grid>
              {!aproe && (
                <Grid item xs={12}>
                  {permissions?.editDescription && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showSpecificationInput}
                          color="secondary"
                          onChange={handleSpecificationToggleChange}
                          name="specificationInputToggle"
                        />
                      }
                      label="I know the exact specification"
                    />
                  )}
                  {showSpecificationInput && (
                    <TestSpecificationInput
                      {...formikProps}
                      disabled={!permissions?.editDescription}
                      handleDirtyChange={handleDirtyChange}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <CustomTextField
              fullWidth
              multiline
              disabled={!permissions?.editDescription}
              error={!!(errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION])}
              helperText={
                errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION]
                  ? errors[Fields.BACKGROUND_INFORMATION]
                  : "Detailed description of the application & purpose of the test request"
              }
              icon={!!(errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION])}
              id={Fields.BACKGROUND_INFORMATION}
              label={Labels.BACKGROUND_INFORMATION}
              name={Fields.BACKGROUND_INFORMATION}
              onBlur={handleBlur}
              onChange={handleLocalChange}
              rows={5}
              value={values[Fields.BACKGROUND_INFORMATION]}
            />
          </Grid>
          <Grid item lg={3} md={3} sm={6} xs={6}>
            <InfoBox text={infoCscLaboratory(permissions?.editDescription, newOrDraft)}>
              <CustomDropdown
                mandatory
                disabled={!permissions?.editDescription || !newOrDraft}
                error={!!(errors[Fields.CSC_LAB] && touched[Fields.CSC_LAB])}
                labelText={Labels.CSC_LAB}
                name={Fields.CSC_LAB}
                onChange={handleLocalChange}
                value={values[Fields.CSC_LAB]}
              >
                <MenuItem value="">
                  <em>{labsLoading ? "Loading..." : "Please Choose"}</em>
                </MenuItem>
                {!labsLoading &&
                  labs.map((lab) => (
                    <MenuItem key={lab.key} value={lab.key}>
                      {lab.name}
                    </MenuItem>
                  ))}
              </CustomDropdown>
            </InfoBox>
          </Grid>
          {aproe && (
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <ProcessTypeInput
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.PROCESS_TYPE] && touched[Fields.PROCESS_TYPE])}
                helperText={
                  errors[Fields.PROCESS_TYPE] && touched[Fields.PROCESS_TYPE] ? errors[Fields.PROCESS_TYPE] : ""
                }
                labelText={Labels.PROCESS_TYPE}
                name={Fields.PROCESS_TYPE}
                onChange={handleLocalChange}
                value={values[Fields.PROCESS_TYPE]}
              />
            </Grid>
          )}
          <Grid item lg={3} md={3} sm={6} xs={6}>
            <CustomDropdown
              mandatory
              disabled={!permissions?.editDescription || isConfidentialityReadOnly}
              error={!!(errors[Fields.CONFIDENTIALITY] && touched[Fields.CONFIDENTIALITY])}
              helperText={
                errors[Fields.CONFIDENTIALITY] && touched[Fields.CONFIDENTIALITY] ? errors[Fields.CONFIDENTIALITY] : ""
              }
              labelText={Labels.CONFIDENTIALITY}
              name={Fields.CONFIDENTIALITY}
              onChange={handleLocalChange}
              onBlur={handleLocalBlur}
              value={values[Fields.CONFIDENTIALITY]}
            >
              <MenuItem value="">
                <em>Please Choose</em>
              </MenuItem>
              <MenuItem value="INTERNAL">Internal (shared internally)</MenuItem>
              <MenuItem value="NORMAL_CONFIDENTIAL">Confidential (shared with the NDA stakeholder)</MenuItem>
              {hasHighlyConfidential && (
                <MenuItem value="HIGHLY_CONFIDENTIAL">Secret project (Attention! Risk of high penalties)</MenuItem>
              )}
            </CustomDropdown>
          </Grid>
          {!aproe && (
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDropdown
                mandatory
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.REPORT_LANGUAGE] && touched[Fields.REPORT_LANGUAGE])}
                helperText={
                  errors[Fields.REPORT_LANGUAGE] && touched[Fields.REPORT_LANGUAGE]
                    ? errors[Fields.REPORT_LANGUAGE]
                    : ""
                }
                labelText={Labels.REPORT_LANGUAGE}
                name={Fields.REPORT_LANGUAGE}
                onChange={handleLocalChange}
                value={values[Fields.REPORT_LANGUAGE]}
              >
                <MenuItem value="">
                  <em>Please Choose</em>
                </MenuItem>
                <MenuItem value="ENGLISH">English</MenuItem>
                <MenuItem value="GERMAN">German</MenuItem>
                <MenuItem value="CHINESE">Chinese</MenuItem>
                <MenuItem value="JAPANESE">Japanese</MenuItem>
                <MenuItem value="KOREAN">Korean</MenuItem>
                <MenuItem value="THAI">Thai</MenuItem>
                <MenuItem value="VIETNAMESE">Vietnamese</MenuItem>
              </CustomDropdown>
            </Grid>
          )}
          <Grid item lg={3} md={3} sm={6} xs={6}>
            {!testRequestHistorical && (
              <DeadlineInput
                deadline={testRequestObj.description?.deadline}
                disabled={!permissions?.editDescription}
                disablePast={!!permissions?.editDescription}
                handleDirtyChange={handleDirtyChange}
                isAproe={aproe}
                showConfirmationDialog={showConfirmationDialog}
                {...formikProps}
              />
            )}
          </Grid>
        </Grid>
        {!newOrDraft && !aproe && (
          <Box>
            <Box height={16} />
            <InfoBox compress text={InfoMessages.CSC_EVALUATION}>
              <h3>CSC Evaluation</h3>
            </InfoBox>
            <Grid container spacing={4}>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <CustomTextField
                  disabled
                  fullWidth
                  error={!!errors[Fields.REPORT_USAGE]}
                  helperText={errors[Fields.REPORT_USAGE] || ""}
                  id={Fields.REPORT_USAGE}
                  label={Labels.REPORT_USAGE}
                  name={Fields.REPORT_USAGE}
                  value={Dictionaries.REPORT_USAGE[values[Fields.REPORT_USAGE]]}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <InfoBox text={InfoMessages.TEST_LOCATION}>
                  <CustomDropdown
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.TEST_LOCATION] && touched.testLocation)}
                    labelText={Labels.TEST_LOCATION}
                    name={Fields.TEST_LOCATION}
                    onChange={handleLocalChange}
                    value={values[Fields.TEST_LOCATION]}
                  >
                    <MenuItem value="">
                      <em>Please Choose</em>
                    </MenuItem>
                    <MenuItem value="CSC_LAB">CSC Lab</MenuItem>
                    <MenuItem value="THIRD_PARTY_LAB">3rd Party Lab</MenuItem>
                  </CustomDropdown>
                </InfoBox>
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <CustomTextField
                  disabled={!permissions?.editAdditional}
                  fullWidth
                  error={!!(errors[Fields.TEST_EFFORT_IN_DAYS] && touched[Fields.TEST_EFFORT_IN_DAYS])}
                  helperText={
                    errors[Fields.TEST_EFFORT_IN_DAYS] && touched[Fields.TEST_EFFORT_IN_DAYS]
                      ? errors[Fields.TEST_EFFORT_IN_DAYS]
                      : "Specify estimated test effort in days"
                  }
                  id={Fields.TEST_EFFORT_IN_DAYS}
                  label={Labels.TEST_EFFORT_IN_DAYS}
                  masked={MASK_NUMBER}
                  name={Fields.TEST_EFFORT_IN_DAYS}
                  onBlur={handleBlur}
                  onChange={handleLocalChange}
                  value={values[Fields.TEST_EFFORT_IN_DAYS]}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                {!testRequestHistorical && (
                  <EstimatedFinishingDateInput
                    estimatedFinishingDate={testRequestObj.description?.estimatedFinishingDate}
                    disabled={!permissions?.editAdditional}
                    disablePast={!!permissions?.editAdditional}
                    handleDirtyChange={handleDirtyChange}
                    showConfirmationDialog={showConfirmationDialog}
                    {...formikProps}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}
        <Box height={16} />
        <TestRequestGallery
          disabled={!permissions?.editAttachments}
          fileReferences={attachedFilesReferences}
          isInCreateMode={createMode}
          onSave={() => handleSave(values).catch(() => scrollToError())}
          testRequestId={testRequestId}
        />
        {showPrefilled && (
          <>
            <Box>
              <Box height={16} />
              <h3>Project Information</h3>
              <Grid container spacing={4}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.BUSINESS_UNIT]}
                    helperText={errors[Fields.BUSINESS_UNIT] || ""}
                    id={Fields.BUSINESS_UNIT}
                    name={Fields.BUSINESS_UNIT}
                    label={Labels.BUSINESS_UNIT}
                    value={values[Fields.BUSINESS_UNIT]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.MARKET_SEGMENT]}
                    helperText={errors[Fields.MARKET_SEGMENT] || ""}
                    id={Fields.MARKET_SEGMENT}
                    name={Fields.MARKET_SEGMENT}
                    label={Labels.MARKET_SEGMENT}
                    value={values[Fields.MARKET_SEGMENT]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.CRM_NO]}
                    helperText={errors[Fields.CRM_NO] || ""}
                    icon={!!errors[Fields.CRM_NO]}
                    id={Fields.CRM_NO}
                    name={Fields.CRM_NO}
                    label={Labels.CRM_NO}
                    value={values[Fields.CRM_NO]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <InfoBox text={InfoMessages.BUSINESS_TYPE}>
                    <CustomTextField
                      disabled
                      fullWidth
                      error={!!errors[Fields.BUSINESS_TYPE]}
                      helperText={errors[Fields.BUSINESS_TYPE] || ""}
                      id={Fields.BUSINESS_TYPE}
                      name={Fields.BUSINESS_TYPE}
                      label={Labels.BUSINESS_TYPE}
                      value={Dictionaries.BUSINESS_TYPE[values[Fields.BUSINESS_TYPE]]}
                    />
                  </InfoBox>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.APPLICATION_FIELD]}
                    helperText={errors[Fields.APPLICATION_FIELD] || ""}
                    id={Fields.APPLICATION_FIELD}
                    name={Fields.APPLICATION_FIELD}
                    label={Labels.APPLICATION_FIELD}
                    value={values[Fields.APPLICATION_FIELD]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.PNS]}
                    helperText={errors[Fields.PNS] || ""}
                    icon={!!errors[Fields.PNS]}
                    id={Fields.PNS}
                    label={Labels.PNS}
                    masked={MASK_THOUSANDS_SEPARATOR}
                    name={Fields.PNS}
                    value={values[Fields.PNS] || ""}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.CURRENCY]}
                    helperText={errors[Fields.CURRENCY] || ""}
                    icon={!!errors[Fields.CURRENCY]}
                    id={Fields.CURRENCY}
                    label={Labels.CURRENCY}
                    name={Fields.CURRENCY}
                    value={values[Fields.CURRENCY] || ""}
                  />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <CustomTextField
                    disabled
                    fullWidth
                    multiline
                    error={!!errors[Fields.SOLUTION]}
                    helperText={errors[Fields.SOLUTION] || ""}
                    icon={!!errors[Fields.SOLUTION]}
                    id={Fields.SOLUTION}
                    label={Labels.SOLUTION}
                    name={Fields.SOLUTION}
                    rows={5}
                    value={values[Fields.SOLUTION]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomDropdown
                    mandatory
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.TEST_PURPOSE] && touched[Fields.TEST_PURPOSE])}
                    id={Fields.TEST_PURPOSE}
                    name={Fields.TEST_PURPOSE}
                    onChange={handleLocalChange}
                    labelText={Labels.TEST_PURPOSE}
                    value={values[Fields.TEST_PURPOSE]}
                  >
                    <MenuItem value="">
                      <em>Please Choose</em>
                    </MenuItem>
                    <MenuItem value="CUSTOMER_TEST">Customer Test</MenuItem>
                    <MenuItem value="RD_SUPPORT">R&D Support</MenuItem>
                    <MenuItem value="MARKETING_SUPPORT">Marketing Support</MenuItem>
                    <MenuItem value="CLAIM_HANDLING">Claim Handling</MenuItem>
                  </CustomDropdown>
                </Grid>
                {permissions?.viewTestRequestPriority && (
                  <Grid item lg={3} md={3} sm={6} xs={6}>
                    <InfoBox text={InfoMessages.PRIORITY}>
                      <CustomDropdown
                        disabled={!permissions?.editDescription}
                        error={!!(errors[Fields.PRIORITY] && touched[Fields.PRIORITY])}
                        id={Fields.PRIORITY}
                        name={Fields.PRIORITY}
                        onChange={handleLocalChange}
                        labelText={Labels.PRIORITY}
                        value={values[Fields.PRIORITY]}
                      >
                        <MenuItem value="NONE" disabled={values[Fields.PRIORITY] !== "NONE"}>
                          <em>NONE</em>
                        </MenuItem>
                        <MenuItem value="LOW">LOW</MenuItem>
                        <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                        <MenuItem value="HIGH">HIGH</MenuItem>
                      </CustomDropdown>
                    </InfoBox>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box>
              <Box height={16} />
              <h3>Customer & Value Chain Information</h3>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomerSearchAutocomplete
                    disabled
                    freeSolo
                    error={!!errors[Fields.DIRECT_CUSTOMER]}
                    helperText={errors[Fields.DIRECT_CUSTOMER] || ""}
                    inputLabel={Labels.DIRECT_CUSTOMER}
                    map={(value): { key: string; label: string } => ({
                      key: `${value.id}`,
                      label: value.name as string,
                    })}
                    onChange={(e, val) => {
                      handleDirtyChange();

                      if (val === null) {
                        const name = (e.target as HTMLInputElement).value || null;
                        setFieldValue(
                          Fields.DIRECT_CUSTOMER,
                          name && {
                            name,
                          },
                        );
                      } else {
                        setFieldValue(Fields.DIRECT_CUSTOMER, val);
                      }
                    }}
                    optionFetch={(query: string) => {
                      return backgroundInformationApi
                        .searchCustomers(values?.directCustomerLocation?.id || 0, query)
                        .then(({ data }) => data as PagedResponse<CustomerResponse>);
                    }}
                    value={values[Fields.DIRECT_CUSTOMER] as CustomerResponse}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.CUSTOMER_CLASSIFICATION]}
                    helperText={errors[Fields.CUSTOMER_CLASSIFICATION] || ""}
                    id={Fields.CUSTOMER_CLASSIFICATION}
                    label={Labels.CUSTOMER_CLASSIFICATION}
                    name={Fields.CUSTOMER_CLASSIFICATION}
                    onBlur={handleBlur}
                    onChange={handleLocalChange}
                    value={values[Fields.CUSTOMER_CLASSIFICATION]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.DEBITOR_NO]}
                    helperText={errors[Fields.DEBITOR_NO] || ""}
                    id={Fields.DEBITOR_NO}
                    label={Labels.DEBITOR_NO}
                    name={Fields.DEBITOR_NO}
                    onBlur={handleBlur}
                    onChange={handleLocalChange}
                    value={values[Fields.DEBITOR_NO]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CountrySearchAutocomplete
                    disabled
                    error={!!errors[Fields.DIRECT_CUSTOMER_LOCATION]}
                    helperText={[errors[Fields.DIRECT_CUSTOMER_LOCATION] || ""].join(" ")}
                    inputLabel={Labels.DIRECT_CUSTOMER_LOCATION}
                    optionFetch={(query: string) => searchLocations(query)}
                    onChange={(e, val): void => {
                      if (values[Fields.DIRECT_CUSTOMER_LOCATION]?.id !== val?.id) {
                        setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, val);
                        setFieldValue(Fields.DIRECT_CUSTOMER, null);
                        dirCustomerSearch.current?.reset();
                        handleDirtyChange();
                      }
                    }}
                    map={(value): { key: string; label: string } => ({
                      key: `${value.id}`,
                      label: value.name as string,
                    })}
                    value={values[Fields.DIRECT_CUSTOMER_LOCATION] as Required<CountryResponse> | null | undefined}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.DIRECT_CUSTOMER_CGR]}
                    helperText={errors[Fields.DIRECT_CUSTOMER_CGR] || ""}
                    id={Fields.DIRECT_CUSTOMER_CGR}
                    name={Fields.DIRECT_CUSTOMER_CGR}
                    label={Labels.DIRECT_CUSTOMER_CGR}
                    value={values[Fields.DIRECT_CUSTOMER_CGR]}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    id={Fields.OEM}
                    label={Labels.OEM}
                    name={Fields.OEM}
                    value={values[Fields.OEM]?.name}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    error={!!errors[Fields.DISTRIBUTOR]}
                    helperText={errors[Fields.DISTRIBUTOR] || ""}
                    id={Fields.DISTRIBUTOR}
                    label={Labels.DISTRIBUTOR}
                    name={Fields.DISTRIBUTOR}
                    value={values[Fields.DISTRIBUTOR]?.name || ""}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box height={16} />
              <h3>Responsibles</h3>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    label="Requester Name"
                    value={testRequestObj?.requester?.userName || user?.userName || ""}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    label="Requester Email"
                    value={testRequestObj?.requestorEmail || user?.userEmail}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomDatePicker
                    disabled
                    label="Submission Date"
                    value={submissionOrApprovalDate}
                    onChange={() => {
                      /* always disabled */
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled
                    fullWidth
                    label="Submitted by"
                    value={testRequestObj?.submitter?.userEmail || ""}
                  />
                </Grid>
                {testRequestObj?.completionDate && (
                  <>
                    <Grid item xs={6}>
                      <CustomTextField
                        disabled
                        fullWidth
                        label="Completion Date"
                        value={testRequestObj?.completionDate}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomTextField
                        disabled
                        fullWidth
                        label="Completed by"
                        value={testRequestObj?.completedBy || ""}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={6}>
                  <CustomTextField disabled fullWidth label="Assignee" value={values.assignee?.userName || ""} />
                </Grid>
                <Grid item xs={6}>
                  <ApproversInput values={testRequestObj?.approvers || []} />
                </Grid>
                <Grid item xs={6}>
                  <InternalContactInput
                    {...formikProps}
                    disabled={!permissions?.editDescription}
                    handleDirtyChange={handleDirtyChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SuggestedApproverInput
                    disabled={
                      !permissions?.editDescription || isStatusPreparationAndFollowing || !values[Fields.CSC_LAB]
                    }
                    error={!!errors[Fields.APPROVER]}
                    helperText={[errors[Fields.APPROVER] || ""].join(" ")}
                    onChange={handleApproverChange}
                    label={Labels.APPROVER}
                    laboratory={values[Fields.CSC_LAB]}
                    value={values[Fields.APPROVER]}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SalesTeamInput values={values[Fields.SALES_TEAM]} />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
        {!showPrefilled && (
          <Box>
            <Box height={16} />
            <h3>Responsibles</h3>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={6}>
                <InternalContactInput
                  {...formikProps}
                  disabled={!permissions?.editDescription}
                  handleDirtyChange={handleDirtyChange}
                />
              </Grid>
              <Grid item xs={6}>
                <SuggestedApproverInput
                  disabled={!permissions?.editDescription || isStatusPreparationAndFollowing || !values[Fields.CSC_LAB]}
                  error={!!errors[Fields.APPROVER]}
                  helperText={[errors[Fields.APPROVER] || ""].join(" ")}
                  onChange={handleApproverChange}
                  label={Labels.APPROVER}
                  laboratory={values[Fields.CSC_LAB]}
                  value={values[Fields.APPROVER]}
                />
              </Grid>
              <Grid item xs={6}>
                <SalesTeamInput values={values[Fields.SALES_TEAM]} />
              </Grid>
            </Grid>
          </Box>
        )}
        <Box height={16} />
      </>
    </Form>
  );
};

export default DescriptionFormSales;
